/*
 * Copyright 2026 Sony Corporation
 */

import { z } from 'zod';

import { CreativeLookSchema, DesiredSchema, ReportedSchema, TransformSchema } from './shadow.js';
import { OpticsSchema } from './optics.js';
import { SceneIdSchema, SceneTransformSchema } from './scenes.js';

export * from './optics.js';

// Part of the API surface already, through the state response and the write
// request; re-exported so a client does not reach past this module for them.
export {
  CreativeLookModeSchema,
  CreativeLookSchema,
  type CreativeLook,
  type CreativeLookMode,
  type Reported,
} from './shadow.js';

/**
 * Every id in this API is server-generated at creation — a client never chooses
 * one, because an id it could choose is an id it could point at someone else's
 * world.
 *
 * They appear in DynamoDB keys, IoT Thing names and the gateway's state file
 * names, and a UUID is safe in all of them: no colon, so the
 * `{deployment}:{world_id}:{device_id}` Thing name cannot be made ambiguous,
 * and well inside the gateway's own cameraIDPattern.
 */
export const IdSchema = z.uuid();

export const RoleSchema = z.enum(['owner', 'editor', 'viewer']);
export type Role = z.infer<typeof RoleSchema>;

/** Roles allowed to change anything. Everyone else is read-only. */
export const WRITER_ROLES: readonly Role[] = ['owner', 'editor'];

export const canWrite = (role: Role): boolean => WRITER_ROLES.includes(role);

export const WorldSchema = z.object({
  world_id: IdSchema,
  name: z.string().min(1).max(120),
  owner_id: z.string(),
  /** The scene this world renders in, once one has been built for it. */
  scene_id: SceneIdSchema.optional(),
  /** Null or absent follows the scene's current revision; a number pins it. */
  scene_revision: z.number().int().nonnegative().nullable().optional(),
  scene_transform: SceneTransformSchema.optional(),
  /**
   * A signed, short-lived URL for the bound scene's thumbnail, so a list of
   * worlds can be shown as pictures rather than names.
   *
   * Computed per response and never stored: the bucket is private, so a URL
   * that outlived its request would be a capability handed out for nothing.
   */
  thumbnail_url: z.string().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type World = z.infer<typeof WorldSchema>;

export const CreateWorldRequestSchema = z
  .object({
    name: z.string().min(1).max(120),
  })
  .strict();
export type CreateWorldRequest = z.infer<typeof CreateWorldRequestSchema>;

export const UpdateWorldRequestSchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
  })
  .strict();
export type UpdateWorldRequest = z.infer<typeof UpdateWorldRequestSchema>;

/**
 * A device's listen address. The port is fixed at 15740 and never carried in the
 * API: PTP clients address a device as a host and assume that port, so letting
 * the cloud express a different one would describe a device the gateway cannot
 * actually run.
 */
export const AddressSchema = z
  .string()
  .regex(/^(\d{1,3}\.){3}\d{1,3}$/, 'must be an IPv4 address')
  .refine((value) => value.split('.').every((octet) => Number(octet) <= 255), 'each octet must be between 0 and 255');

export const DeviceSchema = z.object({
  device_id: IdSchema,
  world_id: IdSchema,
  name: z.string().min(1).max(120),
  model: z.string().min(1).max(60),
  serial: z.string().min(1).max(64),
  address: AddressSchema,
  thing_name: z.string(),
  transform: TransformSchema.optional(),
  /** Absent until someone picks a lens; drawn with `defaultOptics(model)` until then. */
  optics: OpticsSchema.optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Device = z.infer<typeof DeviceSchema>;

export const CreateDeviceRequestSchema = z
  .object({
    device_id: IdSchema.optional(),
    name: z.string().min(1).max(120),
    model: z.string().min(1).max(60),
    /** Omit to have the service generate one: a virtual camera has no printed serial to copy. */
    serial: z.string().min(1).max(64).optional(),
    /** Omit to have the service pick the lowest free 127.0.0.x in the world. */
    address: AddressSchema.optional(),
    transform: TransformSchema.optional(),
    optics: OpticsSchema.optional(),
  })
  .strict();
export type CreateDeviceRequest = z.infer<typeof CreateDeviceRequestSchema>;

export const UpdateDeviceRequestSchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
    model: z.string().min(1).max(60).optional(),
    serial: z.string().min(1).max(64).optional(),
    address: AddressSchema.optional(),
    transform: TransformSchema.optional(),
    optics: OpticsSchema.optional(),
  })
  .strict();
export type UpdateDeviceRequest = z.infer<typeof UpdateDeviceRequestSchema>;

/** Body of a desired-state write. Only these two keys are ever accepted. */
export const WriteStateRequestSchema = z
  .object({
    creative_look: CreativeLookSchema.optional(),
    transform: TransformSchema.optional(),
  })
  .strict()
  .refine(
    (body) => body.creative_look !== undefined || body.transform !== undefined,
    'body must contain creative_look or transform'
  );
export type WriteStateRequest = z.infer<typeof WriteStateRequestSchema>;

export const PathParametersWorldSchema = z.object({ world_id: IdSchema });
export const PathParametersDeviceSchema = z.object({ world_id: IdSchema, device_id: IdSchema });

/**
 * A registered gateway.
 *
 * Only the certificate id is kept. The private key is handed to the user once,
 * in the download, and is never stored here, in S3 or in a log: a key the
 * service cannot reproduce is a key an attacker cannot take from it.
 */
export const GatewaySchema = z.object({
  gateway_id: IdSchema,
  world_id: IdSchema,
  name: z.string().min(1).max(120),
  certificate_id: z.string(),
  policy_name: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  last_seen: z.string().optional(),
});
export type Gateway = z.infer<typeof GatewaySchema>;

/**
 * What a reader is told about a gateway.
 *
 * The certificate id and policy name are management details of a credential and
 * say nothing a user acts on, so they stay on the server: a viewer learning
 * which IoT policy governs a world is an invitation to go looking at it.
 */
export const GatewaySummarySchema = GatewaySchema.pick({
  gateway_id: true,
  world_id: true,
  name: true,
  created_at: true,
  updated_at: true,
  last_seen: true,
});
export type GatewaySummary = z.infer<typeof GatewaySummarySchema>;

export const CreateGatewayRequestSchema = z
  .object({
    gateway_id: IdSchema.optional(),
    name: z.string().min(1).max(120).optional(),
  })
  .strict();
export type CreateGatewayRequest = z.infer<typeof CreateGatewayRequestSchema>;

/**
 * A device's live state.
 *
 * The state block is the shadow document verbatim, camelCase and all: it is the
 * contract shared with the gateway, and rewriting the keys here would leave two
 * spellings of the same thing to keep in step.
 */
export const DeviceStateResponseSchema = z.object({
  device_id: IdSchema,
  thing_name: z.string(),
  state: z.object({
    desired: DesiredSchema.optional(),
    reported: ReportedSchema.optional(),
  }),
  version: z.number().int().optional(),
  /** When the device last reported, in seconds. Absent until it ever has. */
  reported_at: z.number().int().optional(),
});
export type DeviceStateResponse = z.infer<typeof DeviceStateResponseSchema>;

export const WorldsResponseSchema = z.object({ worlds: z.array(WorldSchema) });
export const WorldResponseSchema = z.object({ world: WorldSchema, role: RoleSchema.optional() });
export const DevicesResponseSchema = z.object({ devices: z.array(DeviceSchema) });
export const DeviceResponseSchema = z.object({ device: DeviceSchema });
export const GatewaysResponseSchema = z.object({ gateways: z.array(GatewaySummarySchema) });
export const ErrorResponseSchema = z.object({ message: z.string() });

/** A view-only link to one world (share-world.md). The token itself is never stored or listed. */
export const ShareSchema = z.object({
  share_id: IdSchema,
  world_id: IdSchema,
  label: z.string().max(80).optional(),
  created_by: z.string(),
  created_at: z.string(),
  expires_at: z.string(),
});
export type Share = z.infer<typeof ShareSchema>;

export const MAX_SHARE_DAYS = 30;

export const CreateShareRequestSchema = z
  .object({
    label: z.string().trim().min(1).max(80).optional(),
    expires_in_days: z.number().int().min(1).max(MAX_SHARE_DAYS).default(7),
  })
  .strict();
export type CreateShareRequest = z.input<typeof CreateShareRequestSchema>;

export const PathParametersShareSchema = z.object({ world_id: IdSchema, share_id: IdSchema });

export const ShareResponseSchema = z.object({ share: ShareSchema });
export const CreateShareResponseSchema = z.object({
  share: ShareSchema,
  /** Returned once, here; only its hash is kept. */
  token: z.string(),
});
export type CreateShareResponse = z.infer<typeof CreateShareResponseSchema>;
export const SharesResponseSchema = z.object({ shares: z.array(ShareSchema) });

/** What a link holder sees of a world: no owner, no thumbnail, and always read-only. */
export const SharedWorldSchema = WorldSchema.omit({ owner_id: true, thumbnail_url: true });
export const SharedWorldResponseSchema = z.object({
  world: SharedWorldSchema,
  devices: z.array(DeviceSchema),
  role: z.literal('viewer'),
  expires_at: z.string(),
});
export type SharedWorldResponse = z.infer<typeof SharedWorldResponseSchema>;

/** A JPEG of 4 MB is a 4K still with room to spare; base64 adds a third. */
export const MAX_CAPTURE_BYTES = 4 * 1024 * 1024;

/**
 * A still the browser rendered for a virtual camera's shutter release. Base64
 * in JSON because the API speaks only JSON, and a still is small enough.
 */
export const CreateCaptureRequestSchema = z
  .object({
    request_id: z.string().regex(/^[a-f0-9]{8,64}$/),
    image: z.base64().max(Math.ceil((MAX_CAPTURE_BYTES * 4) / 3) + 4),
  })
  .strict();
export type CreateCaptureRequest = z.infer<typeof CreateCaptureRequestSchema>;
